import { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Skeleton } from '../Skeleton';
import {
    getEngagementSummary,
    type EngagementSummary,
    type EngagementSummarySnapshot,
} from '../../mentorshipService';
type SummaryShape = EngagementSummary | EngagementSummarySnapshot;

function todayISO(): string {
    return new Date().toISOString().slice(0, 10);
}

function ninetyDaysAgoISO(): string {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
}

function MentorEngagementSummaryView() {
    const [userId, setUserId] = useState('');
    const [periodStart, setPeriodStart] = useState(ninetyDaysAgoISO());
    const [periodEnd, setPeriodEnd] = useState(todayISO());
    const [summary, setSummary] = useState<SummaryShape | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        if (!userId.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getEngagementSummary(
                Number(userId),
                periodStart,
                periodEnd,
            );
            setSummary(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load');
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="engagement-summary">
            <Card variant="stacked">
                <Card.Header>
                    <h3>Generate engagement summary</h3>
                </Card.Header>
                <Card.Body>
                    <label htmlFor="es-user-id">Mentee user id</label>
                    <input
                        id="es-user-id"
                        type="number"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                    />
                    <label htmlFor="es-period-start">Period start</label>
                    <input
                        id="es-period-start"
                        type="date"
                        value={periodStart}
                        onChange={(e) => setPeriodStart(e.target.value)}
                    />
                    <label htmlFor="es-period-end">Period end</label>
                    <input
                        id="es-period-end"
                        type="date"
                        value={periodEnd}
                        onChange={(e) => setPeriodEnd(e.target.value)}
                    />
                    <Button
                        variant="primary"
                        size="md"
                        onClick={load}
                        disabled={loading || !userId.trim()}
                    >
                        {loading ? 'Generating…' : 'Generate'}
                    </Button>
                    {error && <p className="report-submit__error">{error}</p>}
                </Card.Body>
            </Card>

            {loading && <Skeleton width="100%" height="200px" borderRadius="12px" />}

            {summary && (
                <Card variant="stacked">
                    <Card.Header>
                        <h3>Summary</h3>
                    </Card.Header>
                    <Card.Body>
                        <h4>Top topics</h4>
                        <ul>{summary.top_topics.map((t, i) => <li key={i}>{t}</li>)}</ul>
                        <h4>Issues</h4>
                        <ul>{summary.issues.map((t, i) => <li key={i}>{t}</li>)}</ul>
                        <h4>Improvements</h4>
                        <ul>{summary.improvements.map((t, i) => <li key={i}>{t}</li>)}</ul>
                        <h4>Effective prompts</h4>
                        <ul>{summary.effective_prompts.map((t, i) => <li key={i}>{t}</li>)}</ul>
                        <h4>Ignored prompts</h4>
                        <ul>{summary.ignored_prompts.map((t, i) => <li key={i}>{t}</li>)}</ul>
                        {summary.mentor_focus && (
                            <>
                                <h4>Mentor focus</h4>
                                <p>{summary.mentor_focus}</p>
                            </>
                        )}
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

export default MentorEngagementSummaryView;
